import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, HostListener, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HighlightTextPipe } from 'src/app/pipe/highlight-text.pipe';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ChatService } from 'src/app/service/chat.service';
import { getTimeAgo, isSystemMessage, isURL, getReplyMessageText, getReplyThumbnail, getReplyMediaType, getMessageMeta, isCodeOrHtml, formatCodeOrHtmlPreview, isVoiceMessage } from '../../utils/helpers.utils';
import { OnlineUsersService } from 'src/app/service/online-users.service';
import { RouterLink } from '@angular/router';
import { ChatDraftService } from 'src/app/service/chat-draft.service';

@Component({
  selector: 'app-chat-sidebar',
  standalone: true,
  imports: [CommonModule, FormsModule, HighlightTextPipe, RouterLink],
  templateUrl: './chat-sidebar.component.html',
  styleUrls: ['./chat-sidebar.component.css']
})
export class ChatSidebarComponent implements OnInit, OnDestroy {
  @Input() chatListLoading: boolean = false;
  @Input() skeletonPlaceholders: any[] = [];

  @Input() allSingleUsers: any[] = [];
  @Input() allGroupUsers: any[] = [];
  @Input() sortedChats: any[] = [];
  @Input() chatMap: { [key: string]: any } = {};
  @Input() userNameMapping: { [key: string]: string } = {};
  @Input() profileImageMapping: { [key: string]: string } = {};

  @Input() userId: string = '';
  @Input() selectedChatUserId: string = '';
  @Input() activeChannel: string = '';
  @Input() memberChannelId: string = '';
  @Input() isMobileView: boolean = false;
  @Input() showChatWindow: boolean = false;
  @Input() showcount: boolean = true;
  @Input() profileImg: string = '';
  @Input() typingUsers: Map<string, { userId: string; userName: string; timestamp: number }> = new Map();
  @Input() starredMessages: any[] = [];

  @Output() chatSelected = new EventEmitter<any>();
  @Output() openCreateGroupModal = new EventEmitter<void>();
  @Output() openAdvancedSearch = new EventEmitter<void>();
  @Output() openStarred = new EventEmitter<void>();
  @Output() starredMessageSelected = new EventEmitter<any>();
  @Output() togglePin = new EventEmitter<any>();

  activeTab: 'all' | 'single' | 'group' | 'unread' | 'starred' = 'all';
  searchQuery: string = '';
  searchResults: any[] = [];
  isSearching: boolean = false;
  searchPage = 1;
  searchLimit = 100;
  hasMoreSearchResults = true;
  lastSearchMessageCreatedAt: string = "";

  searchSubject = new Subject<string>();
  private searchSubscription: Subscription | null = null;
  private draftSubscription: Subscription | null = null;

  showMenuDropdown: boolean = false;
  @ViewChild('dropdownContainer') dropdownContainer!: ElementRef;

  // Per-chat options menu (pin/unpin), keyed by channelId
  chatMenuChannelId: string | null = null;
  private longPressTimer: any = null;
  private suppressNextChatClick = false;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.showMenuDropdown && this.dropdownContainer && !this.dropdownContainer.nativeElement.contains(event.target)) {
      this.showMenuDropdown = false;
    }
    this.chatMenuChannelId = null;
  }

  // Template Helpers
  getReplyMessageText = getReplyMessageText;
  getReplyThumbnail = getReplyThumbnail;
  getReplyMediaType = getReplyMediaType;
  getMessageMeta = getMessageMeta;
  isSystemMessage = isSystemMessage;
  isVoiceMessage = isVoiceMessage;
  constructor(
    private chatService: ChatService,
    private onlineUsersService: OnlineUsersService,
    private chatDraftService: ChatDraftService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((query) => {
        this.handleDebouncedSearch(query);
      });

    this.draftSubscription = this.chatDraftService.draftsUpdated$.subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    if (this.draftSubscription) {
      this.draftSubscription.unsubscribe();
    }
    this.cancelLongPress();
  }

  hasDraft(chat: any): boolean {
    return this.chatDraftService.hasDraftForChat(chat);
  }

  getDraftText(chat: any): string {
    const text = this.chatDraftService.getDraftForChat(chat);
    return (text || '').replace(/\r?\n+/g, ' ').trim();
  }

  hasMessages(chat: any): boolean {
    if (!chat) return false;

    // Keep the currently active/selected chat visible while open
    if (chat.chatType === 'single' && (this.isSingleChatSelected(chat.userId) || this.isSingleChatSelected(chat.channelId))) {
      return true;
    }
    if (chat.chatType === 'group' && this.isGroupChatSelected(chat.channelId)) {
      return true;
    }

    if (chat.isCleared) return false;
    if (chat.unreadCount && chat.unreadCount > 0) return true;

    const lastMsg = chat.lastMessage;
    if (!lastMsg) return false;
    if (this.isNewChatPlaceholder(lastMsg)) return false;

    const messageText = lastMsg.message || lastMsg.msg;
    if (messageText && String(messageText).trim().length > 0) return true;
    if (lastMsg.meta?.fileUrl || lastMsg.fileUrl || lastMsg.url) return true;

    return false;
  }

  private isNewChatPlaceholder(msg: any): boolean {
    return !msg?.sender_id && !msg?.sid && String(msg?.message || '').trim().toLowerCase() === 'new chat created!';
  }

  get filteredChatsByTab(): any[] {
    const activeChats = this.sortedChats.filter(chat => this.hasMessages(chat));
    switch (this.activeTab) {
      case 'all':
        return activeChats;
      case 'single':
        return activeChats.filter(chat => chat.chatType === 'single');
      case 'group':
        return activeChats.filter(chat => chat.chatType === 'group');
      case 'unread':
        return activeChats.filter(chat => chat.unreadCount && chat.unreadCount > 0);
      case 'starred':
        return this.starredMessages;
      default:
        return activeChats;
    }
  }

  get unreadCounts() {
    return {
      all: this.getTotalUnreadCount(),
      single: this.getSingleUnreadCount(),
      group: this.getGroupUnreadCount(),
      unread: this.getTotalUnreadCount()
    };
  }

  private getTotalUnreadCount(): number {
    return this.getSingleUnreadCount() + this.getGroupUnreadCount();
  }

  private getSingleUnreadCount(): number {
    return this.allSingleUsers.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
  }

  private getGroupUnreadCount(): number {
    return this.allGroupUsers.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
  }

  get isSearchActive(): boolean {
    return !!this.searchQuery?.trim();
  }

  get matchedChats() {
    if (!this.searchQuery?.trim()) return [];

    const query = this.searchQuery.toLowerCase();
    return this.sortedChats.filter(chat => {
      const name = (chat.chatType === 'group' ? chat.groupName : (chat.displayName || chat.userName || ''))?.toLowerCase();
      return name.includes(query);
    });
  }

  get matchedMessages() {
    return this.searchQuery?.trim() ? this.searchResults : [];
  }

  toggleMenuDropdown() {
    this.showMenuDropdown = !this.showMenuDropdown;
  }

  onTabSwitch(tab: 'all' | 'single' | 'group' | 'unread' | 'starred') {
    if (tab === 'starred') {
      this.openStarred.emit();
      return;
    }
    this.activeTab = tab;
  }

  onSearchInput() {
    this.searchSubject.next(this.searchQuery);
  }

  handleDebouncedSearch(query: string) {
    if (!query?.trim()) {
      this.searchResults = [];
      this.searchPage = 1;
      this.hasMoreSearchResults = true;
      this.lastSearchMessageCreatedAt = '';
      this.isSearching = false;
      return;
    }

    this.searchResults = [];
    this.searchPage = 1;
    this.lastSearchMessageCreatedAt = '';
    this.hasMoreSearchResults = true;
    this.isSearching = false;
    this.fetchSearchResults();
  }

  trackByChat = (index: number, chat: any): string => {
    return chat?.channelId || chat?.channel_id || chat?.userId || chat?._id || String(index);
  };

  isChatPinned(chat: any): boolean {
    return !!chat?.isPinned;
  }

  async fetchSearchResults() {
    if (!this.hasMoreSearchResults || this.isSearching) return;
    this.isSearching = true;

    let reqSearch: string[] = [];
    if (isURL(this.searchQuery)) {
      try {
        const res: any = await (this.onlineUsersService.isUrlValid({ url: this.searchQuery }) as any).toPromise();
        if (res?.data?.meta) {
          if (res.data.meta.title) reqSearch.push(res.data.meta.title);
          if (res.data.meta.description) reqSearch.push(res.data.meta.description);
        }
      } catch (e) {
        // silently skip - search will still work without meta
      }
    }

    const field: any = {
      pageNum: this.searchPage,
      limit: this.searchLimit,
      searchQuery: this.searchQuery,
    };
    if (reqSearch.length > 0) {
      field.reqSearch = reqSearch;
    }
    if (this.lastSearchMessageCreatedAt) {
      field.createdAt = this.lastSearchMessageCreatedAt;
    }

    this.chatService.searchChatMessages(field).subscribe({
      next: (res: any) => {
        const messages = res?.data?.results || (Array.isArray(res?.data) ? res.data : []);
        if (!messages || !messages.length) {
          this.hasMoreSearchResults = false;
          this.isSearching = false;
          return;
        }

        const enrichedResults = messages.map((msg: any) => {
          const channelId = msg.channel_id || msg.channelId;
          const chat = this.chatMap[channelId];
          const isGroup = msg.chatType === "group" || chat?.chatType === "group" || chat?.type === "group";
          let otherUserId: string | null = null;
          let otherUserName: string | null = null;
          if (!isGroup) {
            if (msg.sender_id === this.userId) {
              otherUserId = chat?.otherUserId || chat?.userId || null;
            } else {
              otherUserId = msg.sender_id;
            }
            otherUserName =
              msg.chatName ||
              this.userNameMapping[otherUserId || ''] ||
              msg.name ||
              "User";
          }

          return {
            ...chat,
            chatType: isGroup ? "group" : "single",
            displayName: isGroup ? (chat?.groupName || msg.displayName) : otherUserName,
            profileImage: msg.profileImg || (isGroup ? chat?.groupImg : (chat?.profileImage || this.profileImageMapping[otherUserId || ''] || this.profileImg)),
            lastMessage: msg,
            lastMessageTimeAgo: this.getTimeAgo(msg.createdAt),
            userId: otherUserId || chat?.userId,
            otherUserId: otherUserId || chat?.userId,
            fromSearch: true
          };
        });

        this.searchResults = [...this.searchResults, ...enrichedResults];
        this.lastSearchMessageCreatedAt = messages[messages.length - 1].createdAt;
        this.searchPage++;
        this.isSearching = false;
      },
      error: (err) => {
        console.error("Search failed:", err);
        this.isSearching = false;
      }
    });
  }

  getTimeAgo(date: any): string {
    return getTimeAgo(date);
  }


  onChatClick(chat: any) {
    this.chatSelected.emit(chat);
  }

  onChatRowClick(chat: any) {
    // The click that follows a long-press should only open the menu, not the chat
    if (this.suppressNextChatClick) {
      this.suppressNextChatClick = false;
      return;
    }
    this.onChatClick(chat);
  }

  openChatMenu(chat: any, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (!chat?.channelId) return;
    this.chatMenuChannelId = this.chatMenuChannelId === chat.channelId ? null : chat.channelId;
  }

  onChatTouchStart(chat: any) {
    this.cancelLongPress();
    this.longPressTimer = setTimeout(() => {
      this.longPressTimer = null;
      if (!chat?.channelId) return;
      this.suppressNextChatClick = true;
      this.chatMenuChannelId = chat.channelId;
      this.cdr.detectChanges();
    }, 500);
  }

  cancelLongPress() {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  onTogglePin(chat: any, event: Event) {
    event.stopPropagation();
    this.chatMenuChannelId = null;
    this.togglePin.emit(chat);
  }

  onCreateGroup() {
    this.showMenuDropdown = false;
    this.openCreateGroupModal.emit();
  }

  onOpenStarred() {
    this.showMenuDropdown = false;
    this.openStarred.emit();
  }

  onClearSearch() {
    this.searchQuery = "";
    this.searchResults = [];
    this.hasMoreSearchResults = false;
    this.lastSearchMessageCreatedAt = "";
  }

  isSingleChatSelected(chatId: string): boolean {
    if (!chatId) return false;
    return this.activeChannel === chatId || this.selectedChatUserId === chatId;
  }

  isGroupChatSelected(channelId: string): boolean {
    if (!channelId) return false;
    return this.memberChannelId === channelId;
  }


  getDisplayLastMessageText(message: any): string {
    if (!message) return '';
    try {
      if (typeof message === 'string') {
        const text = message.trim();
        if (text && isCodeOrHtml(text)) {
          const hasFwdPrefix = text.startsWith('↪ ');
          const cleanText = hasFwdPrefix ? text.slice(2).trim() : text;
          const formatted = formatCodeOrHtmlPreview(cleanText);
          return hasFwdPrefix ? ('↪ ' + formatted) : formatted;
        }
        return text;
      }
      const isFwd = !!(message.meta?.isForwarded || message.forwardedFrom || (typeof message.message === 'string' && message.message.startsWith('↪ ')));
      const text = getReplyMessageText(message);
      if (typeof text === 'string') {
        if (['Photo', 'Video', 'Audio', 'Document'].includes(text)) {
          const article = text === 'Audio' ? 'an' : 'a';
          const res = `sent ${article} ${text.toLowerCase()}`;
          return isFwd && !res.startsWith('↪ ') ? ('↪ ' + res) : res;
        }
        if (text && isCodeOrHtml(text)) {
          const hasFwdPrefix = text.startsWith('↪ ');
          const cleanText = hasFwdPrefix ? text.slice(2).trim() : text;
          const formatted = formatCodeOrHtmlPreview(cleanText);
          return hasFwdPrefix ? ('↪ ' + formatted) : formatted;
        }
        return isFwd && !text.startsWith('↪ ') ? ('↪ ' + text) : text;
      }
      const fallback = typeof message?.message === 'string' ? message.message : '';
      return isFwd && !fallback.startsWith('↪ ') ? ('↪ ' + fallback) : fallback;
    } catch (e) {
      console.error('Error getting display last message text:', e);
      return typeof message?.message === 'string' ? message.message : '';
    }
  }

  getTypingUsersForChat(chat: any): Array<{ userId: string; userName: string }> {
    const channelId = chat.channelId;
    if (!channelId) return [];

    const typingUsers: Array<{ userId: string; userName: string }> = [];
    this.typingUsers.forEach((value, key) => {
      if (key.startsWith(channelId + '_')) {
        typingUsers.push({
          userId: value.userId,
          userName: value.userName
        });
      }
    });

    return typingUsers;
  }

  getTypingTextForChat(chat: any): string {
    const typingUsers = this.getTypingUsersForChat(chat);
    if (typingUsers.length === 0) return '';

    if (typingUsers.length === 1) {
      return `${typingUsers[0].userName} is typing...`;
    } else if (typingUsers.length === 2) {
      return `${typingUsers[0].userName} and ${typingUsers[1].userName} are typing...`;
    } else {
      return `${typingUsers[0].userName} and ${typingUsers.length - 1} others are typing...`;
    }
  }

  isMessageSeen(message: any): boolean {
    if (!message) return false;
    if (message.seen === true) return true;
    if (message.status === 'read' || message.status === 'seen') return true;
    if (message.is_seen === true) return true;
    return false;
  }

  isGroupChat(chat: any): boolean {
    if (!chat) return false;
    return chat.chatType === 'group' || chat.type === 'group' || !!chat.groupName || (Array.isArray(chat.members) && chat.members.length > 0);
  }

  getChatImage(chat: any): string {
    if (!chat) return 'assets/user-icon.png';

    const isGroup = this.isGroupChat(chat);
    if (!isGroup) {
      return chat.profileImage || chat.profileImg || this.profileImageMapping[chat.userId || chat.otherUserId || ''] || 'assets/user-icon.png';
    }

    const isLeadAssignmentGroup = chat.meta?.type === 'Lead Assignment Group' || chat.type === 'Lead Assignment Group' || chat.subType === 'Lead Assignment Group' || chat.meta?.subType === 'Lead Assignment Group';
    const groupImg = chat.groupImg || chat.groupImage || chat.profileImage || this.profileImageMapping[chat.channelId || chat._id || ''];

    if (isLeadAssignmentGroup) {
      return groupImg || 'assets/group_chat.png';
    }

    return chat.groupImg || this.profileImageMapping[chat.channelId || chat._id || ''] || 'assets/group_chat.png';
  }

  handleProfileImageError(event: any, isGroup: boolean = false) {
    event.target.src = isGroup ? 'assets/group_chat.png' : 'assets/user-icon.png';
  }

  handleLinkImageError(event: any) {
    event.target.style.display = 'none';
  }

  handleMediaError(event: any, message: any) {
    const type = this.getReplyMediaType(message.message || message);
    if (type === 'Video') {
      event.target.src = 'assets/video-file.jpg';
    } else if (type === 'Audio') {
      event.target.src = 'assets/audio-thumbnail.png';
    } else {
      event.target.src = 'assets/fileAttachment.png';
    }
  }

  getVoiceDuration(message: any): string {
    const duration = message?.meta?.duration || message?.duration;
    if (!duration) return '';
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `(${minutes}:${seconds < 10 ? '0' : ''}${seconds})`;
  }
}
